import type {
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  Modifier,
  SensorDescriptor,
  SensorOptions,
} from "@dnd-kit/core";
import type { Row as TableRow, Table } from "@tanstack/react-table";
import type { ComponentType, ReactNode } from "react";
import type { z } from "zod";

import type {
  DaySheetQnaSection,
  DaySheetEmployee,
  DaySheetSubcontractor,
  SingleDaySheet,
} from "./roster.interfaces";
import type {
  AddAllocatedFleetSchema,
  CreateRosterFleetSchema,
  PartialContractJobSchema,
  PartialFleetSchema,
  UpdateAllocatedFleetSchema,
} from "./roster.schema";

import type { SubContractorEntity } from "@/app/app.interface";
import type {
  RosterDailyReport,
  RosterDailyReportStatus,
} from "@/features/dailyReport";
import type { FlattenedQuestion } from "@/features/dailyReport/types/flattenQuestions";
import type { Docket } from "@/features/docket/docket.interfaces";
import type { TippingReport } from "@/features/tippingReport";
import type {
  LoadType,
  WasteType,
} from "@/features/wasteManagement/interfaces/wasteType.interfaces";
import type { Workspace } from "@/features/workspace/workspace.interfaces";
import type {
  ExtendedColumnDef,
  FloatingMenuCustomComponentProps,
  WorkForceUserEntity,
} from "@/interfaces/globals";

export enum JobTypes {
  DAY_CONTRACT_JOB = "Day Contract Job",
  DAY_LABOUR_JOB = "Day Labour Job",
  NIGHT_CONTRACT_JOB = "Night Contract Job",
  NIGHT_LABOUR_JOB = "Night Labour Job",
}

export enum ShiftType {
  DAY = "DAY",
  NIGHT = "NIGHT",
  WEEKEND = "WEEKEND",
}

export enum ProjectType {
  CONTRACT = "CONTRACT",
  DOCKET = "DOCKET",
}

export enum DaySheetType {
  DAY_CONTRACT_JOB = "DAY_CONTRACT_JOB",
  DAY_LABOUR_JOB = "DAY_LABOUR_JOB",
  NIGHT_CONTRACT_JOB = "NIGHT_CONTRACT_JOB",
  NIGHT_LABOUR_JOB = "NIGHT_LABOUR_JOB",
  WEEKEND_CONTRACT_JOB = "WEEKEND_CONTRACT_JOB",
  WEEKEND_LABOUR_JOB = "WEEKEND_LABOUR_JOB",
}

export enum ReportTypes {
  DAILY_REPORT = "DAILY_REPORT",
  DOCKET_REPORT = "DOCKET_REPORT",
  TIPPING_RECORDS = "TIPPING_RECORDS",
}

export enum StatusTypes {
  DRAFT = "DRAFT",
}

export enum MenuActions {
  Copy = "Copy",
  Move = "Move",
  Delete = "Delete",
  Send = "Send",
}

export type ReportTypeKey = keyof typeof ReportTypes;

export enum DaySheetStatus {
  EDITED = "EDITED",
  DRAFT = "DRAFT",
  SENT = "SENT",
  SUBMITTED = "SUBMITTED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  CANCELED = "CANCELED",
  DISPUTED = "DISPUTED",
  CLOSED = "CLOSED",
}

export enum ReportSubmissionSource {
  WEB = "WEB",
}

export type SubmittedBy = {
  id: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
};

export type ReportSubmission = {
  id: string;
  counterId: string;
  daySheetId: string;
  daySheet?: {
    siteContact?: Pick<
      WorkForceUserEntity,
      "id" | "firstName" | "lastName"
    > | null;
    startTime?: string | null;
  } | null;
  reportType: ReportTypes;
  status: RosterDailyReportStatus;
  createdAt: string;
  updatedAt: string;
  submittedAt: string;
  submittedById: string;
  submittedBy: SubmittedBy;
  tippingReportId: string | null;
  tippingReport: TippingReport | null;
  workDocketId: string | null;
  docketReport: Docket | null;
  dilapidationReportId: string | null;
  fuelTopupReportId: string | null;
  incidentReportId: string | null;
  dailyReportId: string | null;
  dailyReports: RosterDailyReport | null;
  sections: DaySheetQnaSection[];
  reportSubmissionSource: ReportSubmissionSource;
};

export type SubContractorDaySheet = {
  id: string;
  createdAt: string;
  updatedAt: string;
  subContractorId: string;
  daySheetId: string;
  noe: number;
  subContractor: SubContractorEntity;
  employees: DaySheetSubcontractor[];
};

export type DaySheetWasteDetail = {
  id: string;
  createdAt: string;
  updatedAt: string;
  daySheetId: string;
  wasteTypeId: string;
  loadTypeId: string;
  workType: string;
  quantity: number;
  loadType: LoadType;
  wasteType: WasteType;
};

// ---------------------------------------------------------------------------
// Schema-inferred types (migrated from types/rosterSchema.types.ts)
// ---------------------------------------------------------------------------

export type PartialContractJob = z.infer<typeof PartialContractJobSchema>;
export type PartialFleet = z.infer<typeof PartialFleetSchema>;
export type AddAllocatedFleet = z.infer<typeof AddAllocatedFleetSchema>;
export type CreateRosterFleet = z.infer<typeof CreateRosterFleetSchema>;
export type UpdateAllocatedFleet = z.infer<typeof UpdateAllocatedFleetSchema>;

export type RosterDailyReportPdfProps = {
  reportDetails: RosterDailyReport;
  supervisor?: DaySheetEmployee;
  siteContact?: Pick<WorkForceUserEntity, "id" | "firstName" | "lastName">;
  daySheet?: SingleDaySheet;
  reportTypeLabel?: string;
  flattenedQuestions: FlattenedQuestion[];
  workspace?: Workspace | null;
};

export type Row = {
  label: string;
  value: string;
  images?: string[];
};

export type BuildRowsProps = Pick<
  RosterDailyReportPdfProps,
  | "reportDetails"
  | "supervisor"
  | "siteContact"
  | "daySheet"
  | "reportTypeLabel"
  | "flattenedQuestions"
>;

export type RosterTableEmptyState = {
  title?: string;
  subtitle?: string;
  small?: boolean;
  customLayout?: ReactNode;
};

export type RosterTableSlots = {
  newRowSlot?: ReactNode;
  customEmptyLayout?: ReactNode;
};

export type RosterTableClassNames<TData> = {
  containerClassName?: string;
  tableClassName?: string;
  tableHeaderClassName?: string;
  tableRowClassName?: string;
  getTableRowClassName?: (row: TableRow<TData>) => string;
};

export type RosterTableFloatingMenuConfig = {
  component?: ComponentType<FloatingMenuCustomComponentProps>;
  enabled?: boolean;
  handleUnSelectAll: () => void;
  multiIds: string[];
};

export type RosterTableRowDndConfig<TData> = {
  enabled: boolean;
  disableRowTransform?: boolean;
  dragHandleColumnId?: string;
  dragOverlay?: ReactNode;
  getRowId: (row: TableRow<TData>) => string;
  isRowDisabled?: (row: TableRow<TData>) => boolean;
  modifiers?: Modifier[];
  onDragCancel?: (event: DragCancelEvent) => void;
  onDragEnd?: (event: DragEndEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
  onDragStart?: (event: DragStartEvent) => void;
  sensors?: SensorDescriptor<SensorOptions>[];
};

export type SelectionDomain = "job" | "fleet";

export type TableSelection<TRow = unknown> = {
  domain: SelectionDomain;
  ids: string[];
  rows: TRow[];
};

export type RosterTableSelectionChange<TData> = {
  ids: string[];
  rows: TData[];
};

export type RosterTableSelectionConfig<TData> = {
  tableKey: string;
  resetKey: number;
  onChange: (change: RosterTableSelectionChange<TData>) => void;
};

export type RosterTableProps<TData extends object, TValue = unknown> = {
  classNames?: RosterTableClassNames<TData>;
  columns: ExtendedColumnDef<TData, TValue>[];
  empty?: RosterTableEmptyState;
  floatingMenu?: RosterTableFloatingMenuConfig;
  getRowId?: (row: TData, index: number) => string;
  onTableInstanceChange?: (table: Table<TData>) => void;
  rowDndConfig?: RosterTableRowDndConfig<TData>;
  rows: TData[];
  selection?: RosterTableSelectionConfig<TData>;
  slots?: RosterTableSlots;
  title: string;
};
